import { useState, useCallback } from 'react';
import { Play, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VoiceMemoListItem } from './voice-memo-list-item';
import type { VoiceMemo } from '@/lib/api';

export interface VoiceMemoGroup {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  memos: VoiceMemo[];
}

interface VoiceMemoSessionGroupProps {
  group: VoiceMemoGroup;
  onProcess: (filename: string) => void;
  onProcessAll?: (filenames: string[]) => void;
  onViewTranscript?: (memo: VoiceMemo) => void;
  onReviewAndFile?: (memo: VoiceMemo) => void;
  transcribingFilenames?: Set<string>;
  selectedMemoId?: string | null;
  defaultExpanded?: boolean;
}

export function VoiceMemoSessionGroup({
  group,
  onProcess,
  onProcessAll,
  onViewTranscript,
  onReviewAndFile,
  transcribingFilenames,
  selectedMemoId,
  defaultExpanded = true
}: VoiceMemoSessionGroupProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const formatTime = (timestamp: string) => {
    if (timestamp.length >= 4) {
      return `${timestamp.substring(0, 2)}:${timestamp.substring(2, 4)}`;
    }
    return timestamp;
  };

  // Find memos that need transcription (no transcript exists, regardless of status)
  const memosNeedingTranscription = group.memos.filter(m => !m.transcript?.path);
  const hasMemosNeedingTranscription = memosNeedingTranscription.length > 0;
  const isTranscribingAny = group.memos.some(m =>
    transcribingFilenames?.has(m.filename) || m.status === 'transcribing'
  );

  const handleTranscribeAll = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent collapsing when clicking button
    if (onProcessAll && memosNeedingTranscription.length > 0) {
      onProcessAll(memosNeedingTranscription.map(m => m.filename));
    }
  };

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <div className="mb-4 border border-border rounded-lg bg-card overflow-hidden">
      {/* Collapsible Header */}
      <div
        role="button"
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded(); } }}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors group cursor-pointer"
      >
        <div className="flex items-center gap-2 flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          )}
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider group-hover:text-foreground transition-colors">
            Session • {group.date} • {formatTime(group.startTime)} - {formatTime(group.endTime)} • {group.memos.length} memos
          </div>
        </div>
        <div className="flex-1" />
        {hasMemosNeedingTranscription && onProcessAll && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleTranscribeAll}
            disabled={isTranscribingAny}
            className="h-7 text-xs flex-shrink-0"
          >
            {isTranscribingAny ? (
              <>
                <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                Transcribing...
              </>
            ) : (
              <>
                <Play className="w-3 h-3 mr-1.5" />
                Transcribe All ({memosNeedingTranscription.length})
              </>
            )}
          </Button>
        )}
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="border-t border-border bg-muted/20">
          <div className="px-4 py-3 space-y-2">
            {group.memos.map(memo => (
              <VoiceMemoListItem
                key={memo.id}
                memo={memo}
                onProcess={onProcess}
                onViewTranscript={onViewTranscript}
                onReviewAndFile={onReviewAndFile}
                isSelected={selectedMemoId === memo.id}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

